<style src="./AssociateItem.scss" lang="scss"></style>
<template>
  <section class="associate-item">
    <section class="item-description">
      <div class="id">
        <span class="badge lot" v-if="!!info.itemInfo.lot">LOTE {{ info.itemInfo.lot }}</span>
        <span class="badge number">ITEM {{ info.itemInfo.number }}</span>
      </div>

      <div class="description-container">
        <span
          v-for="(wordItem, wordIdx) in wordSeparated"
          :key="wordIdx"
          class="description"
          :class="{ 'clickable-word': wordItem.length > 2 }"
          @click="selectWord(wordItem)">
          {{ wordItem }}
        </span>
      </div>

      <div class="iteminfo">
        <span class="number quantity"><small>QT:</small> <strong>{{ info.itemInfo.quantity | DecimalNumber }}</strong></span>
        <span class="number factor"><small>x</small><strong>{{ info.itemInfo.factor | DecimalNumber }}</strong></span>
        <span class="number estimated"><small>ESTIMADO: R$</small> <strong>{{ info.itemInfo.estimated | CurrencyBR('Não Informado') }}</strong></span>
      </div>
    </section>
  
    <div class="head">
      <h5 class="description">Para associar um item, utiliza a busca abaixo e ao encontrar o item desejado, clique nele para que a escolha seja efetivada.</h5>
      <button
        type="button"
        @click="cancelEdit"
        class="cp1-xsmall-button cp1-button__danger">
        Cancelar Edição
      </button>
    </div>
    <textbox
      class="finder"
      debounce="300"
      @debounce="doSearch"
      v-model="searchBox"
      placeholder="Busca o produto que deseja"
      label="Produto" />

    <ul class="list">
      <li v-if="!listItems.length">Nenhum item encontrado com essa descrição</li>
      <li
        v-for="( item, itemIdx ) in listItems"
        :key="itemIdx"
        @click="chooseItem(item)"
        class="item">

        <div class="identification">
          <div class="labcod">
            <span class="lab">{{ item.lab }}</span>
            <span class="cod" title="Código Interno">{{ item.codProd }}</span>
          </div>
          <span class="pativo">{{ item.pativo }}</span>
          <span class="comercialname">{{ item.comercialName }}</span>
        </div>

        <div class="price">
          <div class="line cost">
            <span class="title">CUSTO</span>
            <span
              class="value">
              {{ item.cost | CurrencyBR(null, 4) }}
            </span>
          </div>
          <div class="line cmed">
            <span class="title">CMED</span>
            <span class="value">{{ item.lookupCmed && item.lookupCmed.computedValue | CurrencyBR('---', 4) }}</span>
          </div>
          <div class="line lastbuy">
            <span class="title">ULT. COMPRA</span>
            <span class="value">{{ item.lastBuyingDate | DateBR }}</span>
          </div>
          <div class="line lastbuy">
            <span class="title">QT. ESTOQUE</span>
            <span class="value">{{ item.stockQuantity | DecimalNumber(null, null, 0) }}</span>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import { Textbox } from '@/components/primitives'
import { SearchStock } from '@/brain/record/stock'
import { CurrencyBR, DateBR, DecimalNumber } from '@/components/primitives/helpers/filters'
import PreparePayloadToSelect from '../../helpers/PreparePayloadToSelect'

export default {
  name: 'AssociateItem',
  components: {
    Textbox
  },
  props: {
    info: {
      default: () => ({})
    }
  },
  data: () => ({
    listItems: [],
    searchBox: ''
  }),
  filters: {
    CurrencyBR,
    DateBR,
    DecimalNumber
  },
  computed: {
    wordSeparated () {
      return this.info.itemInfo.description.split(' ')
    }
  },
  mounted () {
    this.setDefaultProduct()
  },
  methods: {
    async doSearch (pSearchTerm) {
      try {
        const stock = await SearchStock({
          params: {
            skip: 0,
            limit: 15,
            filters: {
              searchText: pSearchTerm
            }
          }
        })

        this.listItems = stock.list.map(i => ({
          ...i,
          cost: i.cost * this.info.itemInfo.factor,
          lookupCmed: {
            ...i.lookupCmed,
            computedValue: i.lookupCmed && i.lookupCmed.computedValue * this.info.itemInfo.factor
          },
          priceEdition: false
        }))
      } catch (error) {
        this.listItems = []
      }
    },
    async chooseItem (pItem) {
      const itemId = this.info.itemId
      const stockId = pItem._id
      await this.$store.dispatch('ItemPricing/ASSOCIATE_ITEM', { stockId, itemId })
      const eventId = this.$route.params.id
      if (!eventId) return false
      await this.$store.dispatch('ItemPricing/LIST_ALL_ITEMS', { eventId })

      const { associated } = this.$store.getters['ItemPricing/GET_ITEM_BY_QUOTE_ID'](this.info.itemId)
      const isAssociated = this.isChoosedAssociatedItem()
      if (isAssociated) {
        const proposalMargin = this.$store.getters['ItemPricing/PROPOSAL_MARGIN']
        const decimalPrecision = this.$store.getters['ItemPricing/DECIMAL_PRECISION']

        const quotationPayload = PreparePayloadToSelect({
          item: associated,
          info: this.info,
          proposalMargin,
          decimalPrecision
        })
        await this.saveChoice(quotationPayload)
      }
      this.$emit('finish-edition')
    },
    async saveChoice (quotationPayload) {
      try {
        const payload = {
          quote: quotationPayload.item,
          ...quotationPayload
        }
        const info = await this.$store.dispatch('ItemPricing/SELECT_QUOTE_ITEM', payload)
        this.$store.commit('ItemPricing/SET_ITEM', {
          itemId: this.info.itemId,
          info
        })

        this.$toasted.success('Item reassociado com sucesso')
        return true
      } catch (error) {
        this.$toasted.error('Não foi possível salvar')
        return false
      }
    },
    cancelEdit () {
      this.$emit('finish-edition')
    },
    selectWord (pWord) {
      const word = pWord.replace(/[^a-zA-ZÀ-ú0-9]/gi, ' ')
      const wordNormalized = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      this.searchBox += ' ' + wordNormalized
      this.doSearch(this.searchBox)
    },
    setDefaultProduct () {
      if (this.info.associated && this.info.associated.pativo) {
        this.searchBox = this.info.associated.pativo
        this.doSearch(this.searchBox)
      }
    },
    isChoosedAssociatedItem () {
      const quotedPrice = this.info.quoted.price
      const quotedItemId = this.info.quoted.itemId

      const associatedPrice = this.info.associated.price
      const associatedItemId = this.info.associated.itemId

      if (quotedPrice === associatedPrice && quotedItemId === associatedItemId) {
        return true
      }

      return false
    }
  }
}
</script>


// WEBPACK FOOTER //
// src/view/item-pricing/components/associate-item/AssociateItem.vue